import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import AppSelect from '@nxsys/components/AppSelect';
import AppsContainer from '@nxsys/components/AppsContainer';
import { AppState } from '@nxsys/store';
import { getGreetingDashboard } from '@nxsys/utils';
import YearEndAlert from '@nxsys/components/YearEndProcess/YearEndAlert';
import { Col, Row } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ClientApprovalReject from './ClientApprovalReject';
import Communication from './Communication';
import EmployeeStatus from './EmployeeStatus';
import EmployerIWantTo from './EmployerIWantTo';
import EmployerTaxPeriod from './EmployerTaxPeriod';
import Miscellaneous from './Miscellaneous';
import Payments from './Payments';
import PayrollImport from './PayrollImport';
import PensionContributionBureau from './PensionContributionBureau';
import RTISubmission from './RTISubmission';
import CurrentEmploymentYTD from './components/CurrentEmploymentYTD';
import DocumentNearingExpiry from './components/DocumentNearingExpiry';
import EmployeeDashboardTimesheet from './components/EmployeeDashboardTimesheet';
import EmployeeOtherItem from './components/EmployeeOtherItem';
import EmployeePensionContribution from './components/EmployeePensionContribution';
import HolidayPayBalance from './components/HolidayPayBalance';
import PayBreakdown from './components/PayBreakdown';
import PayslipHistory from './components/PayslipHistory';
import UpcomingPayment from './components/UpcomingPayment';
import BannerCarouselSection from './components/BannerCarouselSection';
import ActionRequiredSection from '../analytics/components/bureau/ActionRequiredSection';
import {
  DashboardEmployerAnalyticsProvider,
  useDashboardEmployerAnalytics,
} from './context/DashboardEmployerAnalyticsContext';
import type { ICompany } from '@nxsys/types/models/company';
import { companyActions } from '@nxsys/store/slices/company/reducer/companySlice';
import ActionRequiredSectionView from './components/ActionRequiredSectionView';

const EmployerDashboardRightContent = ({
  isSingleCompany,
}: {
  isSingleCompany: boolean;
}) => {
  const { setParams } = useDashboardEmployerAnalytics();
  const dispatch = useDispatch();
  const { companyList } = useSelector((state: AppState) => state.companyStore);
  const companies: ICompany[] = companyList.list;

  const companyOptions = useMemo(
    () =>
      companies
        .filter((c) => !!c?.id)
        .map((c) => ({
          label: c.companyName ?? c.tradingName ?? c.id,
          value: c.id,
        })),
    [companies],
  );

  // Fetch companies once for employer dashboard
  useEffect(() => {
    if (isSingleCompany) return;
    if (!companies.length) {
      dispatch(
        companyActions.getCompanyList({
          page: 0,
          size: 200,
        }),
      );
    }
  }, [companies.length, isSingleCompany]);

  return (
    <div className="flex items-center gap-x-[8px]">
      {!isSingleCompany && (
        <AppSelect
          options={companyOptions}
          placeholder="Company"
          onChange={(opt: { label: string; value: string }) => {
            setParams((prev) => ({
              ...(prev ?? {}),
              employerIds: opt?.value || undefined,
            }));
          }}
        />
      )}
      <AppPayFrequencySelect
        onChange={(payFrequency) => {
          setParams((prev) => ({
            ...(prev ?? {}),
            payFrequencies: payFrequency || undefined,
          }));
        }}
      />
    </div>
  );
};

const DashboardUK = () => {
  const { user, isBureauEmployer } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isSingleCompany = user?.companyUserIds?.length === 1;
  const isSubcontractor = user?.roles === 'subcontractor';
  // Employer-only accounts manage just their own payroll, so the bureau
  // client-management widgets (Pending Employer Approval / Client Approval &
  // Reject) do not apply to them.
  const isEmployerOnly = !!user?.isEmployerOnly;

  const employerInitialParams = useMemo(() => ({}), []);

  const renderTitle = useCallback(() => {
    return (
      <>
        <AppPageTitle
          separator=""
          prefix={getGreetingDashboard()}
          title={`${user?.forename || ''} ${user?.surname || ''}`}
        />
        <YearEndAlert />
      </>
    );
  }, [user]);

  const renderRightContent = useCallback(() => {
    if (isBureauEmployer) {
      return (
        <EmployerDashboardRightContent isSingleCompany={isSingleCompany} />
      );
    }
    return <></>;
  }, [isBureauEmployer, isSingleCompany]);

  const renderDashboardByRole = useCallback(() => {
    // Employer
    if (isBureauEmployer) {
      return (
        <>
          <Row gutter={[16, 16]}>
            <Col sm={24} md={16}>
              <EmployerTaxPeriod />
            </Col>
            <Col sm={24} md={8}>
              <ActionRequiredSection />
            </Col>
          </Row>
          {/* <EmployerActivePayRunWithContext /> */}
          <Row gutter={[16, 16]}>
            <Col sm={24} md={12}>
              <EmployeeStatus />
            </Col>
            <Col sm={24} md={12}>
              <EmployerIWantTo />
            </Col>
          </Row>
          {!isEmployerOnly && <PayrollImport />}
          <Row gutter={[16, 16]} className="!mb-6">
            {!isEmployerOnly && (
              <Col sm={24} md={12}>
                <ClientApprovalReject />
              </Col>
            )}
            <Col sm={24} md={isEmployerOnly ? 24 : 12}>
              <Payments />
            </Col>
          </Row>
        </>
      );
    }

    // Subcontractor
    if (isSubcontractor) {
      const subcontractorId = (user?.subcontractorId as any)?.id;

      return (
        <>
          <BannerCarouselSection />
          <Row gutter={[16, 16]}>
            <Col sm={24} md={6} className="w-full">
              <UpcomingPayment
                subcontractorId={subcontractorId}
                isCisOrNonCis={false}
              />
            </Col>
            <Col sm={24} md={12} className="w-full">
              <PayBreakdown />
            </Col>
            <Col sm={24} md={6} className="w-full">
              <PayslipHistory />
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col sm={24} md={12} className="w-full">
              <CurrentEmploymentYTD />
            </Col>
            <Col sm={24} md={12} className="w-full">
              <EmployeeOtherItem />
            </Col>
          </Row>
          <EmployeeDashboardTimesheet />
          <Row gutter={[16, 16]}>
            <Col sm={24} md={12} className="w-full">
              <HolidayPayBalance />
            </Col>
            <Col sm={24} md={12} className="w-full">
              <EmployeePensionContribution />
            </Col>
          </Row>
          <DocumentNearingExpiry subcontractorId={subcontractorId} />
        </>
      );
    }

    // Admin
    return (
      <>
        <BannerCarouselSection />
        <Miscellaneous />
        <PayrollImport />
        <Row gutter={24}>
          <Col sm={24} md={16}>
            <ClientApprovalReject />
          </Col>
          <Col sm={24} md={8}>
            <ActionRequiredSectionView />
          </Col>
        </Row>
        <Payments />
        <Communication />
        <RTISubmission />
        <PensionContributionBureau />
      </>
    );
  }, [isBureauEmployer, isSubcontractor, isEmployerOnly]);

  const content = (
    <AppsContainer
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={false}
      fullView
    >
      <div className="space-y-[24px] pb-[24px]">{renderDashboardByRole()}</div>
    </AppsContainer>
  );

  return isBureauEmployer ? (
    <DashboardEmployerAnalyticsProvider initialParams={employerInitialParams}>
      {content}
    </DashboardEmployerAnalyticsProvider>
  ) : (
    content
  );
};

export default DashboardUK;
